import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { wsUrl } from "../api/client";
import { backoffDelay } from "../lib/backoff";
import { SilenceGate } from "./dsp";
import { encodePcm, parseFrame } from "./frames";
import { VoiceAudio, type MicResult } from "./engine";
import { applyServerMessage, EMPTY, parseServerMessage, voiceMode, type Person, type VoiceMode, type VoicePerson, type VoiceState } from "./voiceState";

// Team voice (docs/API.md section 9): one open-mic channel, like a group
// call. Join and your mic is open; anyone can talk at once; mute stops
// sending without leaving. One WebSocket to /ws/radio for the whole app
// gives the roster before you join. The mic runs only while you are in voice.

export type RadioConn = "connecting" | "open" | "reconnecting";

export interface Radio {
  conn: RadioConn;
  mode: VoiceMode;
  inVoice: boolean;
  muted: boolean;
  online: Person[];
  voice: VoicePerson[];
  /** User ids heard in the last 300 ms, including you while your level is above the gate. */
  speaking: ReadonlySet<string>;
  meId: string;
  /** One plain line (errors, connection), or null. */
  message: string | null;
  /** Audio is blocked until the next tap or key (browser autoplay rules). */
  needsGesture: boolean;
  join: () => void;
  leave: () => void;
  toggleMute: () => void;
}

const Ctx = createContext<Radio | null>(null);

const PING_MS = 25_000;
const SPEAKING_MS = 300;

function micMessage(r: MicResult): string {
  switch (r) {
    case "denied":
      return "microphone blocked. you can listen; allow the mic for this site in the browser's settings to talk";
    case "unsupported":
      return "no microphone available here. you can listen";
    case "busy":
      return "the microphone is busy in another app. you can listen";
    default:
      return "could not open the microphone. you can listen";
  }
}

function isTyping(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT";
}

export function RadioProvider({ token, meId, children }: { token: string; meId: string; children: ReactNode }) {
  const [conn, setConn] = useState<RadioConn>("connecting");
  const [voice, setVoice] = useState<VoiceState>(EMPTY);
  const [joining, setJoining] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [speaking, setSpeaking] = useState<ReadonlySet<string>>(new Set());
  const [running, setRunning] = useState(false);

  const audio = useMemo(() => new VoiceAudio(), []);
  const wsRef = useRef<WebSocket | null>(null);
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  /** You asked to be in voice (survives reconnects until you leave). */
  const wantVoice = useRef(false);
  /** Your mute choice, applied before the server confirms so no audio slips out. */
  const mutedRef = useRef(false);
  const gate = useRef(new SilenceGate());
  const heardAt = useRef(new Map<string, number>());

  const send = useCallback((data: string | ArrayBuffer) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(data);
  }, []);

  // ---- Socket ----
  useEffect(() => {
    let ws: WebSocket | null = null;
    let attempt = 0;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let ping: ReturnType<typeof setInterval> | null = null;
    let disposed = false;

    const schedule = () => {
      if (disposed) return;
      setConn("reconnecting");
      retry = setTimeout(connect, backoffDelay(attempt++));
    };

    const connect = () => {
      if (disposed) return;
      try {
        ws = new WebSocket(wsUrl("/ws/radio", { token }));
      } catch {
        schedule();
        return;
      }
      const sock = ws;
      sock.binaryType = "arraybuffer";
      wsRef.current = sock;
      sock.onopen = () => {
        attempt = 0;
        setConn("open");
        // Disconnecting left voice on the server; a dropped link rejoins as you were.
        if (wantVoice.current) sock.send(JSON.stringify({ type: "join", muted: mutedRef.current }));
        ping = setInterval(() => {
          if (sock.readyState === WebSocket.OPEN) sock.send(JSON.stringify({ type: "ping" }));
        }, PING_MS);
      };
      sock.onmessage = (ev: MessageEvent) => {
        if (typeof ev.data === "string") {
          const m = parseServerMessage(ev.data);
          if (!m) return;
          const next = applyServerMessage(voiceRef.current, m);
          if (next === voiceRef.current) return;
          voiceRef.current = next;
          setVoice(next);
          if (next.you.in_voice) {
            setJoining(false);
            mutedRef.current = next.you.muted;
          }
          return;
        }
        if (ev.data instanceof ArrayBuffer) {
          const f = parseFrame(ev.data);
          if (!f || f.userId === meId || !voiceRef.current.you.in_voice) return;
          heardAt.current.set(f.userId, Date.now());
          audio.play(f);
        }
      };
      sock.onclose = () => {
        if (wsRef.current === sock) wsRef.current = null;
        if (ping) clearInterval(ping);
        ping = null;
        const reset = { ...voiceRef.current, you: { in_voice: false, muted: mutedRef.current } };
        voiceRef.current = reset;
        setVoice(reset);
        schedule();
      };
      sock.onerror = () => undefined;
    };

    const onOnline = () => {
      if (!ws || ws.readyState === WebSocket.CLOSED) {
        if (retry) clearTimeout(retry);
        attempt = 0;
        connect();
      }
    };
    window.addEventListener("online", onOnline);
    connect();
    return () => {
      disposed = true;
      window.removeEventListener("online", onOnline);
      if (retry) clearTimeout(retry);
      if (ping) clearInterval(ping);
      if (ws) {
        ws.onclose = null;
        ws.close(1000, "bye");
      }
      wsRef.current = null;
    };
  }, [token, meId, audio]);

  // ---- Audio: unlock on the first gesture; gated mic frames out ----
  useEffect(() => {
    const unlock = () => void audio.unlock();
    window.addEventListener("pointerdown", unlock, { capture: true });
    window.addEventListener("keydown", unlock, { capture: true });
    const off = audio.subscribe(() => {
      setRunning(audio.running);
      // The mic went away mid-call (unplugged, revoked): mute rather than pretend.
      if (wantVoice.current && voiceRef.current.you.in_voice && !mutedRef.current && !audio.micOpen) {
        mutedRef.current = true;
        send(JSON.stringify({ type: "mute", muted: true }));
        setMessage("the microphone stopped. unmute to try again");
      }
    });
    audio.onFrame = (pcm) => {
      if (!voiceRef.current.you.in_voice || mutedRef.current) return;
      if (gate.current.pass(pcm)) send(encodePcm(pcm));
      if (gate.current.open) heardAt.current.set(meId, Date.now());
    };
    return () => {
      window.removeEventListener("pointerdown", unlock, { capture: true });
      window.removeEventListener("keydown", unlock, { capture: true });
      off();
      audio.onFrame = null;
      audio.dispose();
    };
  }, [audio, send, meId]);

  // The mic runs only while you are in voice (kept through a brief reconnect,
  // since the socket rejoins you as you were).
  const inVoice = voice.you.in_voice;
  useEffect(() => {
    if (!inVoice && !wantVoice.current && audio.micOpen) audio.closeMic();
  }, [inVoice, audio]);

  // Speaking dots, a few times a second (300 ms after the last frame).
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      const ids = new Set<string>();
      heardAt.current.forEach((at, id) => {
        if (now - at < SPEAKING_MS) ids.add(id);
      });
      setSpeaking((prev) => (prev.size === ids.size && [...ids].every((i) => prev.has(i)) ? prev : ids));
    }, 100);
    return () => clearInterval(t);
  }, []);

  // ---- Controls ----
  const join = useCallback(() => {
    if (wantVoice.current && (voiceRef.current.you.in_voice || joining)) return;
    setMessage(null);
    setJoining(true);
    void audio.openMic().then((r) => {
      wantVoice.current = true;
      gate.current = new SilenceGate();
      // No mic: join anyway, muted, so you can still listen.
      const muted = r !== "ok";
      mutedRef.current = muted;
      if (muted) setMessage(micMessage(r));
      send(JSON.stringify({ type: "join", muted }));
      setTimeout(() => {
        if (wantVoice.current && !voiceRef.current.you.in_voice) {
          setJoining(false);
          setMessage("the voice server did not answer. try again");
          wantVoice.current = false;
          audio.closeMic();
        }
      }, 6000);
    });
  }, [audio, send, joining]);

  const leave = useCallback(() => {
    wantVoice.current = false;
    setJoining(false);
    send(JSON.stringify({ type: "leave" }));
    audio.closeMic();
    heardAt.current.clear();
  }, [audio, send]);

  const setMuted = useCallback(
    (muted: boolean) => {
      if (!voiceRef.current.you.in_voice) return;
      if (muted) {
        mutedRef.current = true; // stop sending this instant
        send(JSON.stringify({ type: "mute", muted: true }));
        return;
      }
      void audio.openMic().then((r) => {
        if (r !== "ok") {
          setMessage(micMessage(r));
          return;
        }
        setMessage(null);
        gate.current = new SilenceGate();
        mutedRef.current = false;
        send(JSON.stringify({ type: "mute", muted: false }));
      });
    },
    [audio, send],
  );

  const toggleMute = useCallback(() => setMuted(!mutedRef.current), [setMuted]);

  // M toggles mute while in voice (never while typing in a field).
  useEffect(() => {
    if (!inVoice) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "m" || e.repeat || e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
      e.preventDefault();
      toggleMute();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inVoice, toggleMute]);

  const mode = voiceMode(voice, joining);
  const text = message ?? (conn !== "open" ? (conn === "connecting" ? "voice connecting" : "voice offline · reconnecting") : null);

  const value = useMemo<Radio>(
    () => ({
      conn,
      mode,
      inVoice,
      muted: voice.you.muted,
      online: voice.online,
      voice: voice.voice,
      speaking,
      meId,
      message: text,
      needsGesture: !running,
      join,
      leave,
      toggleMute,
    }),
    [conn, mode, inVoice, voice, speaking, meId, text, running, join, leave, toggleMute],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRadio(): Radio {
  const v = useContext(Ctx);
  if (!v) throw new Error("useRadio outside RadioProvider");
  return v;
}
